import axios from 'axios';
import Cookies from 'js-cookie';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const API = `${API_BASE}/api`;

// Create axios instance with auth header
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = Cookies.get('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      Cookies.remove('auth_token');
      Cookies.remove('user');
      if (window.location.pathname.startsWith('/admin')) {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(error);
  }
);

// ============== AUTH ==============
export const authApi = {
  login: async (username, password) => {
    const response = await apiClient.post('/auth/login', { username, password });
    return response.data;
  },
  
  logout: async () => {
    await apiClient.post('/auth/logout');
  },
  
  verify: async () => {
    const response = await apiClient.get('/auth/verify');
    return response.data;
  },
  
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.post('/auth/admin/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    return response.data;
  },
};

// ============== POSTS ==============
export const postsApi = {
  // Public
  getPublicPosts: async (params = {}) => {
    const response = await axios.get(`${API}/posts/public`, { params });
    return response.data;
  },
  
  getFeaturedPosts: async (limit = 3) => {
    const response = await axios.get(`${API}/posts/public/featured`, { params: { limit } });
    return response.data;
  },
  
  getLatestPosts: async (limit = 8) => {
    const response = await axios.get(`${API}/posts/public/latest`, { params: { limit } });
    return response.data;
  },
  
  getPostBySlug: async (slug) => {
    const response = await axios.get(`${API}/posts/public/by-slug/${slug}`);
    return response.data;
  },
  
  // Admin
  getAllPosts: async (params = {}) => {
    const response = await apiClient.get('/posts', { params });
    return response.data;
  },
  
  getPost: async (id) => {
    const response = await apiClient.get(`/posts/${id}`);
    return response.data;
  },
  
  createPost: async (data) => {
    const response = await apiClient.post('/posts', data);
    return response.data;
  },
  
  updatePost: async (id, data) => {
    const response = await apiClient.put(`/posts/${id}`, data);
    return response.data;
  },
  
  deletePost: async (id) => {
    const response = await apiClient.delete(`/posts/${id}`);
    return response.data;
  },
  
  publishPost: async (id) => {
    const response = await apiClient.post(`/posts/${id}/publish`);
    return response.data;
  },
  
  unpublishPost: async (id) => {
    const response = await apiClient.post(`/posts/${id}/unpublish`);
    return response.data;
  },
  
  generateSlug: async (title) => {
    const response = await apiClient.post('/posts/generate-slug', { title });
    return response.data;
  },
};

// ============== CATEGORIES ==============
export const categoriesApi = {
  // Public
  getPublicCategories: async () => {
    const response = await axios.get(`${API}/categories/public`);
    return response.data;
  },
  
  // Admin
  getCategories: async () => {
    const response = await apiClient.get('/categories');
    return response.data;
  },
  
  createCategory: async (data) => {
    const response = await apiClient.post('/categories', data);
    return response.data;
  },
  
  updateCategory: async (id, data) => {
    const response = await apiClient.put(`/categories/${id}`, data);
    return response.data;
  },
  
  deleteCategory: async (id) => {
    const response = await apiClient.delete(`/categories/${id}`);
    return response.data;
  },
};

// ============== TAGS ==============
export const tagsApi = {
  // Public
  getPublicTags: async () => {
    const response = await axios.get(`${API}/tags/public`);
    return response.data;
  },
  
  // Admin
  getTags: async () => {
    const response = await apiClient.get('/tags');
    return response.data;
  },
  
  createTag: async (data) => {
    const response = await apiClient.post('/tags', data);
    return response.data;
  },
  
  deleteTag: async (id) => {
    const response = await apiClient.delete(`/tags/${id}`);
    return response.data;
  },
};

// ============== REACTIONS ==============
export const reactionsApi = {
  getLabels: async () => {
    const response = await axios.get(`${API}/reactions/labels`);
    return response.data;
  },
  
  getPostReactions: async (postId, clientId) => {
    const headers = clientId ? { 'x-reaction-client-id': clientId } : {};
    const response = await axios.get(`${API}/reactions/post/${postId}`, { headers });
    return response.data;
  },
  
  addReaction: async (postId, reactionType, clientId) => {
    const headers = clientId ? { 'x-reaction-client-id': clientId } : {};
    const response = await axios.post(
      `${API}/reactions/`,
      {
        post_id: postId,
        reaction_type: reactionType,
      },
      { headers },
    );
    return response.data;
  },
  
  removeReaction: async (postId, reactionType) => {
    const response = await axios.delete(`${API}/reactions/${postId}/${reactionType}`);
    return response.data;
  },
};

// ============== STATS ==============
export const statsApi = {
  getStats: async () => {
    const response = await apiClient.get('/stats');
    return response.data;
  },
};

// ============== NEWSLETTER ==============
export const newsletterApi = {
  subscribe: async (email) => {
    const response = await axios.post(`${API}/newsletter/subscribe`, { email });
    return response.data;
  },
  getSubscribers: async (params = {}) => {
    const response = await apiClient.get('/admin/subscribers', { params });
    return response.data;
  },
  deleteSubscriber: async (id) => {
    const response = await apiClient.delete(`/admin/subscribers/${id}`);
    return response.data;
  },
};

// ============== CONTACT MESSAGES ==============
export const contactApi = {
  sendMessage: async (data) => {
    const response = await axios.post(`${API}/contact`, data);
    return response.data;
  },
  getMessages: async (params = {}) => {
    const response = await apiClient.get('/admin/messages', { params });
    return response.data;
  },
  getMessage: async (id) => {
    const response = await apiClient.get(`/admin/messages/${id}`);
    return response.data;
  },
  updateReadState: async (id, read) => {
    const response = await apiClient.patch(`/admin/messages/${id}`, null, { params: { read } });
    return response.data;
  },
  deleteMessage: async (id) => {
    const response = await apiClient.delete(`/admin/messages/${id}`);
    return response.data;
  },
};

// ============== MEDIA ==============
export const mediaApi = {
  uploadImage: async (file, alt) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('alt', alt);
    const response = await apiClient.post('/uploads/images', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data; // { id, url, width, height, alt }
  },
};

// ============== CMS ==============
export const cmsApi = {
  getHomepage: async () => {
    const response = await axios.get(`${API}/cms/homepage`);
    return response.data;
  },
  updateHomepage: async (data) => {
    const response = await apiClient.put('/cms/homepage', data);
    return response.data;
  },
  getAbout: async () => {
    const response = await axios.get(`${API}/cms/about`);
    return response.data;
  },
  updateAbout: async (data) => {
    const response = await apiClient.put('/cms/about', data);
    return response.data;
  },
  getNewsletter: async () => {
    const response = await axios.get(`${API}/cms/newsletter`);
    return response.data;
  },
  updateNewsletter: async (data) => {
    const response = await apiClient.put('/cms/newsletter', data);
    return response.data;
  },
  getFooter: async () => {
    const response = await axios.get(`${API}/cms/footer`);
    return response.data;
  },
  updateFooter: async (data) => {
    const response = await apiClient.put('/cms/footer', data);
    return response.data;
  },
};

export default apiClient;
